import React, { useCallback, JSX } from "react";
import { strings } from "../../resources/strings";
import { Appointment } from "../../apis/API";
import LoadingIndicator from "../shared/LoadingIndicator";
import { ExpandableInstructions } from "../index";
import { formatDateTime, formatCountdownText } from "../../utils/dateTimeUtils";

const INSTRUCTION_LINE_LIMIT = 1; // Truncate instructions at exactly one line for consistent behavior

export interface ApptTileProps {
  appt?: Appointment;
  court?: Appointment;
  isLoading?: boolean;
}

const ApptInfoTile: React.FC<ApptTileProps> = ({ appt, court, isLoading }) => {
  const getDetailedContent = useCallback(
    (item: Appointment, showAppointmentType: boolean = false): JSX.Element => {
      const instructionsText = item.appointmentInstructions || item.notes || "";

      return (
        <div className="flex flex-col space-y-4">
          {showAppointmentType && item.resolvedAppointmentType?.label && (
            <div className="text-left">
              <span className="font-bold">
                {strings.appointmentLabels.what}
              </span>{" "}
              {item.resolvedAppointmentType.label}
            </div>
          )}
          <div className="text-left">
            {item.time && (
              <>
                {(() => {
                  const formattedDateTime = formatDateTime(
                    item.time,
                    item.endTime || undefined,
                  );
                  const countdownText = formatCountdownText(item.time);
                  const formattedCountdownText = `(${countdownText})`;

                  if (formattedDateTime.includes("between")) {
                    return (
                      <div>
                        <div>
                          <span className="font-bold">
                            {strings.appointmentLabels.when}
                          </span>{" "}
                          {formattedDateTime}
                        </div>
                        <div className="ml-14 text-slate-600">
                          {formattedCountdownText}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <span>
                        <span className="font-bold">
                          {strings.appointmentLabels.when}
                        </span>{" "}
                        {formattedDateTime}{" "}
                        <span className="text-slate-600">
                          {formattedCountdownText}
                        </span>
                      </span>
                    );
                  }
                })()}
              </>
            )}
          </div>
          {item.location && (
            <>
              <div className="text-left">
                <div>
                  <span className="font-bold">
                    {strings.appointmentLabels.where}
                  </span>{" "}
                  {(item.location.number || item.location.street) && (
                    <>
                      {item.location.number ? `${item.location.number} ` : ""}
                      {item.location.street || ""}
                    </>
                  )}
                </div>
                {(item.location.city ||
                  item.location.state ||
                  item.location.postalCode) && (
                  <div className="ml-14">
                    {item.location.city ? `${item.location.city}` : ""}
                    {item.location.city && item.location.state ? ", " : ""}
                    {item.location.state ? `${item.location.state} ` : ""}
                    {item.location.postalCode || ""}
                  </div>
                )}
                {item.location.name && (
                  <div className="font-bold mt-1">{item.location.name}</div>
                )}
              </div>
            </>
          )}
          <div className="text-left">
            <span className="font-bold">
              {strings.appointmentLabels.instructions}
            </span>{" "}
            <ExpandableInstructions
              instructionsText={instructionsText}
              lineLimit={INSTRUCTION_LINE_LIMIT}
              textColor="text-black"
              textStyle={{ color: "#193A3A" }}
            />
          </div>
        </div>
      );
    },
    [],
  );

  // Show loading state
  if (isLoading) {
    const isAppointmentType = appt !== undefined;
    const titleText = isAppointmentType
      ? strings.appointments.appointmentTileTitle
      : strings.appointments.courtTileTitle;

    const IconComponent = isAppointmentType ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
        />
      </svg>
    );

    return (
      <div
        className="card bg-[#F5F5F5] text-black border-2"
        style={{ borderColor: "#193A3A" }}
      >
        <div className="card-body py-5 px-6">
          {/* h3: tile titles sit inside a section whose header is an h2. */}
          <h3
            className="card-title flex items-center"
            style={{ color: "#193A3A" }}
          >
            <span className="mr-2" style={{ color: "#193A3A" }}>
              {IconComponent}
            </span>
            {titleText}
          </h3>
          <div
            className="py-8"
            role="status"
            aria-label="Loading appointment data"
          >
            <LoadingIndicator customSize="40px" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card bg-[#F5F5F5] text-black border-2"
      style={{ borderColor: "#193A3A" }}
    >
      <div className="card-body py-5 px-6">
        {/* Render appointment tile */}
        {appt && (
          <>
            <h3
              className="card-title flex items-center"
              style={{ color: "#193A3A" }}
            >
              <span className="mr-2" style={{ color: "#193A3A" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                  />
                </svg>
              </span>
              {strings.appointments.appointmentTileTitle}
            </h3>
            <div className="pt-5" style={{ color: "#193A3A" }}>
              {getDetailedContent(appt, true)}
            </div>
          </>
        )}

        {/* Render the Upcoming Court Date tile */}
        {court && (
          <>
            <h3
              className="card-title flex items-center"
              style={{ color: "#193A3A" }}
            >
              <span className="mr-2" style={{ color: "#193A3A" }}>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
                  />
                </svg>
              </span>
              {strings.appointments.courtTileTitle}
            </h3>
            <div className="pt-5" style={{ color: "#193A3A" }}>
              {getDetailedContent(court, false)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApptInfoTile;
